import { supabase } from '../../../lib/supabase'
import {
  META_COLS,
  DuplicateGroup,
  DuplicateRecord,
  GroupDecision,
  ProgressStore,
  MergeStats,
  GroupSummary,
  StatusFilter,
  Confidence,
} from '../types'

export class MergeService {
  private static instance: MergeService
  private groups: Map<string, DuplicateGroup> = new Map()
  private groupIds: string[] = []
  private companyFields: string[] = []
  private progress: ProgressStore = {}
  private isLoaded = false

  static getInstance(): MergeService {
    if (!MergeService.instance) {
      MergeService.instance = new MergeService()
    }
    return MergeService.instance
  }

  async loadData(): Promise<void> {
    if (this.isLoaded) return

    try {
      const response = await fetch('/merge-review/DUPLICATE_RECORDS_FOR_REVIEW.csv')
      const csvText = await response.text()
      this.parseCSV(csvText)
      await this.loadProgress()
      this.isLoaded = true
    } catch (error) {
      console.error('Error loading merge review data:', error)
      throw error
    }
  }

  private parseCSV(csvText: string): void {
    const lines = csvText.trim().split('\n')
    if (lines.length < 2) return

    const headers = this.parseCSVLine(lines[0])
    this.companyFields = headers.filter(h => !META_COLS.has(h.trim()))

    const groupsMap = new Map<string, { meta: any; records: DuplicateRecord[] }>()

    for (let i = 1; i < lines.length; i++) {
      const values = this.parseCSVLine(lines[i])
      const row: Record<string, string> = {}
      headers.forEach((h, idx) => {
        row[h.trim()] = (values[idx] || '').trim()
      })

      const gid = row['duplicate_group_id']
      if (!gid) continue

      if (!groupsMap.has(gid)) {
        groupsMap.set(gid, {
          meta: {
            size: parseInt(row['group_size']) || 0,
            confidence: row['confidence'] as Confidence,
            matchReasons: row['match_reasons'] || '',
          },
          records: [],
        })
      }

      const fields: Record<string, string> = {}
      for (const col of this.companyFields) {
        fields[col] = row[col] || ''
      }

      groupsMap.get(gid)!.records.push({
        lineNumber: parseInt(row['line_number']) || 0,
        fields,
      })
    }

    this.groups.clear()
    groupsMap.forEach((data, gid) => {
      this.groups.set(gid, {
        groupId: gid,
        size: data.meta.size,
        confidence: data.meta.confidence,
        matchReasons: data.meta.matchReasons,
        records: data.records,
        suggestedMerge: this.suggestMergedValues(data.records),
      })
    })

    this.groupIds = Array.from(this.groups.keys()).sort()
  }

  private parseCSVLine(line: string): string[] {
    const result: string[] = []
    let current = ''
    let inQuotes = false

    for (let i = 0; i < line.length; i++) {
      const char = line[i]
      if (char === '"') {
        inQuotes = !inQuotes
      } else if (char === ',' && !inQuotes) {
        result.push(current)
        current = ''
      } else {
        current += char
      }
    }
    result.push(current)
    return result
  }

  private suggestMergedValues(records: DuplicateRecord[]): Record<string, string> {
    const merged: Record<string, string> = {}
    for (const field of this.companyFields) {
      const vals = records
        .map(r => (r.fields[field] || '').trim())
        .filter(Boolean)
      if (!vals.length) {
        merged[field] = ''
        continue
      }
      const uniq = Array.from(new Set(vals))
      if (uniq.length === 1) {
        merged[field] = uniq[0]
      } else {
        merged[field] = vals.sort((a, b) => b.length - a.length)[0]
      }
    }
    return merged
  }

  private async loadProgress(): Promise<void> {
    try {
      const { data, error } = await supabase
        .from('merge_review_progress')
        .select('group_id, action, merged_values')

      if (error) {
        console.error('Error loading progress from Supabase:', error)
        return
      }

      this.progress = {}
      if (data) {
        for (const row of data) {
          this.progress[row.group_id] = {
            action: row.action as GroupDecision['action'],
            mergedValues: row.merged_values || undefined,
          }
        }
      }
    } catch (error) {
      console.error('Error loading progress:', error)
      this.progress = {}
    }
  }

  getGroupIds(): string[] {
    return this.groupIds
  }

  getCompanyFields(): string[] {
    return this.companyFields
  }

  getGroup(groupId: string): DuplicateGroup | null {
    return this.groups.get(groupId) || null
  }

  getGroupStatus(groupId: string): GroupDecision['action'] | 'pending' {
    return this.progress[groupId]?.action || 'pending'
  }

  getSavedMergedValues(groupId: string): Record<string, string> | undefined {
    return this.progress[groupId]?.mergedValues
  }

  getGroupSummaries(): GroupSummary[] {
    return this.groupIds.map(gid => {
      const g = this.groups.get(gid)!
      return {
        id: gid,
        size: g.size,
        confidence: g.confidence,
        status: this.getGroupStatus(gid),
        names: g.records.map(r => r.fields['Company Name'] || ''),
      }
    })
  }

  getFilteredGroupIds(filter: StatusFilter): string[] {
    if (filter === 'all') return this.groupIds
    return this.groupIds.filter(gid => this.getGroupStatus(gid) === filter)
  }

  getStats(): MergeStats {
    const total = this.groupIds.length
    let merged = 0, skipped = 0, notDuplicates = 0
    for (const gid of this.groupIds) {
      const action = this.progress[gid]?.action
      if (action === 'merged') merged++
      else if (action === 'skipped') skipped++
      else if (action === 'not_duplicates') notDuplicates++
    }
    return {
      total,
      merged,
      skipped,
      notDuplicates,
      pending: total - merged - skipped - notDuplicates,
    }
  }

  async decide(groupId: string, action: GroupDecision['action'], mergedValues?: Record<string, string>): Promise<void> {
    const mergedVals = action === 'merged' ? mergedValues : undefined

    // Update local state immediately for responsive UI
    this.progress[groupId] = { action, mergedValues: mergedVals }

    // Persist to Supabase
    const { data: userData } = await supabase.auth.getUser()
    const userId = userData?.user?.id

    const { error } = await supabase
      .from('merge_review_progress')
      .upsert({
        group_id: groupId,
        action,
        merged_values: mergedVals || null,
        decided_by: userId || null,
        updated_at: new Date().toISOString(),
      }, { onConflict: 'group_id' })

    if (error) {
      console.error('Error saving decision to Supabase:', error)
      // Revert local state on failure
      delete this.progress[groupId]
      throw new Error('Failed to save decision')
    }
  }

  getProgress(): ProgressStore {
    return { ...this.progress }
  }

  getGroups(): Map<string, DuplicateGroup> {
    return this.groups
  }
}

export const mergeService = MergeService.getInstance()
